/**
 * Parses content module keys in the format "title_id" where id is a UUID
 * @param key - The key in format "title_uuid"
 * @returns Object with title, id, and original key
 */
export function parseContentModuleKey(key: string): {
  title: string
  id: string
  originalKey: string
} {
  // Find the last underscore - this should separate title from UUID
  const lastUnderscoreIndex = key.lastIndexOf('_')

  if (lastUnderscoreIndex === -1) {
    // No underscore found, treat entire string as title
    return {
      title: key,
      id: '',
      originalKey: key,
    }
  }

  const potentialId = key.substring(lastUnderscoreIndex + 1)
  const potentialTitle = key.substring(0, lastUnderscoreIndex)

  // Check if the part after the last underscore looks like a UUID
  // UUID format: 8-4-4-4-12 hexadecimal characters
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  if (uuidPattern.test(potentialId)) {
    return {
      title: potentialTitle,
      id: potentialId,
      originalKey: key,
    }
  }

  // If it doesn't match UUID pattern, treat entire string as title
  return {
    title: key,
    id: '',
    originalKey: key,
  }
}

/**
 * Transforms the byContentModule object from the API into filter options
 * @param contentModulesData - Object with "title_id" keys and count values
 * @returns Array of filter options with id as value and "title (count)" as label
 */
export function createContentModuleFilterOptions(contentModulesData: Record<string, number>) {
  return Object.entries(contentModulesData)
    .map(([key, count]) => {
      const { title, id } = parseContentModuleKey(key)
      return {
        value: id || key, // Use ID if available, fallback to original key
        label: `${title} (${count})`,
        title,
        id,
        count,
      }
    })
    .filter(option => option.value) // Remove any empty values
    .sort((a, b) => a.title.localeCompare(b.title)) // Sort by title alphabetically
}

/**
 * Transforms the byTopic stats object into filter options
 * @param byTopicData - Object with topic IDs as keys and count values
 * @param topicsData - Array of topic objects with id and displayName
 * @returns Array of filter options with topic ID as value and "Topic Name (count)" as label
 */
export function createTopicFilterOptions(
  byTopicData: Record<string, number>,
  topicsData: Array<{ id: string; displayName: string }>,
) {
  return Object.entries(byTopicData)
    .map(([topicId, count]) => {
      const topic = topicsData.find(t => t.id === topicId)
      const topicName = topic ? topic.displayName : topicId // fallback to ID if not found
      return {
        value: topicId,
        label: `${topicName} (${count})`,
        topicName,
        topicId,
        count,
      }
    })
    .filter(option => option.value) // Remove any empty values
    .sort((a, b) => a.topicName.localeCompare(b.topicName)) // Sort by topic name alphabetically
}
